import { useState } from 'react';

type CoachCTAType = 'insight' | 'action' | 'schedule';

type Props = {
  type: CoachCTAType;
  fields: Record<string, string>;
  onSave: (payload: Record<string, string>) => Promise<void> | void;
  onSkip: () => void;
};

export default function CoachPromptCard({ type, fields, onSave, onSkip }: Props) {
  const [timeframe, setTimeframe] = useState<string>(fields.timeframe || 'this week');
  const [priority, setPriority] = useState<string>((fields.priority || 'medium').toLowerCase());

  const title = type === 'insight' ? 'Save Insight?' : type === 'action' ? 'Add Action?' : 'Next Connect?';
  const primary = '#4C62E3';
  const label = fields.label || fields.text || '';

  const timeframeOptions = type === 'schedule' ? ['This week', 'Next week', 'In 2 weeks', 'Later'] : ['Today', 'This week', 'Next week'];

  const handlePrimary = async () => {
    if (type === 'insight') {
      await onSave({ text: label });
      return;
    }
    if (type === 'action') {
      await onSave({ label, timeframe, priority, status: 'todo' });
      return;
    }
    if (type === 'schedule') {
      if ((timeframe || '').toLowerCase() === 'later') {
        onSkip();
        return;
      }
      await onSave({ label: label || 'Next coaching connect', timeframe });
      return;
    }
  };

  return (
    <div className="mt-2 w-full rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-bold text-slate-700">{title}</p>
      </div>
      {label ? (
        <p className="mb-2 text-sm text-slate-800">{label}</p>
      ) : null}
      {type !== 'insight' && (
        <div className="mb-2 flex flex-wrap gap-2">
          {timeframeOptions.map((opt) => (
            <button
              key={opt}
              onClick={() => setTimeframe(opt.toLowerCase())}
              className={`rounded-full px-3 py-1 text-xs font-medium border ${
                (timeframe || '').toLowerCase() === opt.toLowerCase()
                  ? 'bg-[#4C62E3] text-white border-transparent'
                  : 'bg-slate-100 text-slate-700 border-slate-200'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
      {type === 'action' && (
        <div className="mb-2 flex gap-2">
          {['low', 'medium', 'high'].map((p) => (
            <button
              key={p}
              onClick={() => setPriority(p)}
              className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider border ${
                priority === p ? 'bg-white text-slate-900 border-slate-300' : 'bg-slate-50 text-slate-600 border-slate-200'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      )}
      <div className="mt-2 flex items-center gap-2">
        <button
          onClick={handlePrimary}
          className="rounded-lg px-3 py-1.5 text-xs font-semibold bg-[#4C62E3] text-white hover:bg-[#3B4CC5]"
        >
          Save
        </button>
        <button
          onClick={onSkip}
          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"
        >
          Skip
        </button>
      </div>
    </div>
  );
}
